import { filter, map, pipe } from "rxjs";
import log from "loglevel";
import { filter as filter$1, map as map$1 } from "rxjs/operators";

//#region src/core/errors.ts
var UnexpectedError = class extends Error {
	constructor(at, options) {
		super(`Unexpected Error${at ? ` at ${at}` : ""}`, options);
		this.at = at;
		this.name = "UnexpectedError";
	}
};
var UnimplementedError = class extends Error {
	constructor(reason = "Not Implemented", options) {
		super(reason, options);
		this.reason = reason;
		this.name = "UnimplementedError";
	}
};
var InvalidCredentialsError = class extends Error {
	constructor(reason = "Invalid Credentials", options) {
		super(reason, options);
		this.reason = reason;
		this.name = "InvalidCredentialsError";
	}
};
var WebSocketConnectionError = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.name = "WebSocketConnectionError";
	}
};
var TransportConnectionError = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.name = "TransportConnectionError";
	}
};
var WebSocketTimeoutError = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.name = "WebSocketTimeoutError";
	}
};
var RequestTimeoutError = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.name = "RequestTimeoutError";
	}
};
var RequestError = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.name = "RequestError";
	}
};
var RPCTimeoutError = class extends Error {
	constructor(requestId, timeoutMs, options) {
		super(`RPC request ${requestId} timed out after ${timeoutMs}ms`, options);
		this.requestId = requestId;
		this.timeoutMs = timeoutMs;
		this.name = "RPCTimeoutError";
	}
};
var AuthStateHandlerError = class extends Error {
	constructor(error = null, options) {
		super("Error handling authorization state update", {
			...options,
			cause: options?.cause ?? (error instanceof Error ? error : void 0)
		});
		this.error = error;
		this.name = "AuthStateHandlerError";
	}
};
var StorageNotAvailableError = class extends Error {
	constructor(storageType = "localStorage", options) {
		super(`${storageType} is not available in this environment`, options);
		this.storageType = storageType;
		this.name = "StorageNotAvailableError";
	}
};
var SerializationError = class extends Error {
	constructor(key, originalError) {
		super(`Failed to serialize value for key "${key}": ${originalError.message}`, { cause: originalError });
		this.key = key;
		this.originalError = originalError;
		this.name = "SerializationError";
	}
};
var DeserializationError = class extends Error {
	constructor(key, originalError) {
		super(`Failed to deserialize value for key "${key}": ${originalError.message}`, { cause: originalError });
		this.key = key;
		this.originalError = originalError;
		this.name = "DeserializationError";
	}
};
var StorageWriteError = class extends Error {
	constructor(key, originalError) {
		super(`Failed to write to storage for key "${key}": ${originalError.message}`, { cause: originalError });
		this.key = key;
		this.originalError = originalError;
		this.name = "StorageWriteError";
	}
};
var StorageReadError = class extends Error {
	constructor(key, originalError) {
		super(`Failed to read from storage "${key}": ${originalError.message}`, { cause: originalError });
		this.key = key;
		this.originalError = originalError;
		this.name = "StorageReadError";
	}
};
var DependencyError = class extends Error {
	constructor(description, options) {
		super(`Dependency ${description} is not set or available.`, options);
		this.description = description;
		this.name = "DependencyError";
	}
};
var CallCreateError = class extends Error {
	constructor(message, error = null, direction = "outbound", options) {
		super(message, {
			...options,
			cause: options?.cause ?? (error instanceof Error ? error : void 0)
		});
		this.message = message;
		this.error = error;
		this.direction = direction;
		this.name = "CallCreateError";
	}
};
var JSONRPCError = class extends Error {
	constructor(code, message, data, options, requestId) {
		super(message, options);
		this.code = code;
		this.data = data;
		this.requestId = requestId;
		this.name = "JSONRPCError";
	}
};
var InvalidParams = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.message = message;
		this.name = "InvalidParams";
	}
};
var ConversationError = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.message = message;
		this.name = "ConversationError";
	}
};
var VertoInviteHandlerError = class extends Error {
	constructor(error = null, options) {
		super("Error handling Verto invite", {
			...options,
			cause: options?.cause ?? (error instanceof Error ? error : void 0)
		});
		this.error = error;
		this.name = "VertoInviteHandlerError";
	}
};
var VertoAttachHandlerError = class extends Error {
	constructor(error = null, options) {
		super("Error handling Verto attach", {
			...options,
			cause: options?.cause ?? (error instanceof Error ? error : void 0)
		});
		this.error = error;
		this.name = "VertoAttachHandlerError";
	}
};
var ValidationError = class extends Error {
	constructor(message, options) {
		super(message, options);
		this.name = "ValidationError";
	}
};
var VertoPongError = class extends Error {
	constructor(originalError) {
		super("Failed to send Verto pong - call may disconnect", { cause: originalError instanceof Error ? originalError : void 0 });
		this.originalError = originalError;
		this.name = "VertoPongError";
	}
};
var MessageParseError = class extends Error {
	constructor(originalError) {
		super("Failed to parse incoming WebSocket message", { cause: originalError instanceof Error ? originalError : void 0 });
		this.originalError = originalError;
		this.name = "MessageParseError";
	}
};
var CollectionFetchError = class extends Error {
	constructor(operation, originalError) {
		super(`Collection fetch failed during ${operation}`, { cause: originalError instanceof Error ? originalError : void 0 });
		this.operation = operation;
		this.originalError = originalError;
		this.name = "CollectionFetchError";
	}
};
var MediaTrackError = class extends Error {
	constructor(operation, kind, originalError) {
		super(`Media track ${operation} failed for ${kind}`, { cause: originalError instanceof Error ? originalError : void 0 });
		this.operation = operation;
		this.kind = kind;
		this.originalError = originalError;
		this.name = "MediaTrackError";
	}
};
var DPoPInitError = class extends Error {
	constructor(originalError, message = "Failed to initialize DPoP key pair") {
		super(message, { cause: originalError instanceof Error ? originalError : void 0 });
		this.originalError = originalError;
		this.name = "DPoPInitError";
	}
};
/**
* Error thrown when a recovery attempt fails.
*
* Carries the recovery action and attempt number for diagnostic purposes.
*/
var RecoveryError = class extends Error {
	constructor(action, attempt, originalError) {
		super(`Recovery failed: ${action} (attempt ${attempt})`, { cause: originalError instanceof Error ? originalError : void 0 });
		this.action = action;
		this.attempt = attempt;
		this.originalError = originalError;
		this.name = "RecoveryError";
	}
};
/**
* Error thrown when getUserMedia fails with OverconstrainedError
* and all fallback levels have been exhausted.
*/
var OverconstrainedFallbackError = class extends Error {
	constructor(deviceKind, originalError) {
		super(`All constraint fallback levels exhausted for ${deviceKind}`, { cause: originalError instanceof Error ? originalError : void 0 });
		this.deviceKind = deviceKind;
		this.originalError = originalError;
		this.name = "OverconstrainedFallbackError";
	}
};
/**
* Error thrown when the preflight connectivity test fails.
*/
var PreflightError = class extends Error {
	constructor(phase, originalError) {
		super(`Preflight test failed during ${phase}`, { cause: originalError instanceof Error ? originalError : void 0 });
		this.phase = phase;
		this.originalError = originalError;
		this.name = "PreflightError";
	}
};
var DeviceTokenError = class extends Error {
	constructor(message, originalError) {
		super(message, { cause: originalError instanceof Error ? originalError : void 0 });
		this.originalError = originalError;
		this.name = "DeviceTokenError";
	}
};
var TokenRefreshError = class extends Error {
	constructor(message, originalError) {
		super(message, { cause: originalError instanceof Error ? originalError : void 0 });
		this.originalError = originalError;
		this.name = "TokenRefreshError";
	}
};

//#endregion
//#region src/utils/logger.ts
const datetime = () => (/* @__PURE__ */ new Date()).toISOString();
const defaultLogger = log.getLogger("signalwire");
const originalFactory = defaultLogger.methodFactory;
defaultLogger.methodFactory = (methodName, logLevel, loggerName) => {
	const rawMethod = originalFactory(methodName, logLevel, loggerName);
	return function(...args) {
		const prefixed = [
			datetime(),
			"-",
			...args
		];
		rawMethod.apply(void 0, prefixed);
	};
};
const defaultLoggerLevel = defaultLogger.levels.WARN;
defaultLogger.setLevel(defaultLoggerLevel);
let userLogger = null;
/** Replace the built-in logger with a custom implementation. Pass `null` to restore defaults. */
const setLogger = (logger$1) => {
	userLogger = logger$1;
};
let debugOptions = {};
/** Configure debug options (e.g., `{ logWsTraffic: true }`). */
const setDebugOptions = (options) => {
	if (options == null) {
		debugOptions = {};
		return;
	}
	debugOptions = {
		...debugOptions,
		...options
	};
};
/**
* Set the log level for the built-in logger.
* Has no effect when a custom logger is set via `setLogger()`.
*/
const setLogLevel = (level) => {
	defaultLogger.setLevel(level);
};
const getLoggerInstance = () => {
	return userLogger ?? defaultLogger;
};
const shouldStringify = (payload) => {
	if (payload != null && typeof payload === "object" && "method" in payload) return payload.method !== "signalwire.ping";
	return true;
};
const wsTraffic = (options) => {
	const { logWsTraffic } = debugOptions;
	if (!logWsTraffic) return;
	const loggerInstance = getLoggerInstance();
	let payload;
	if ("raw" in options) try {
		payload = JSON.parse(options.raw);
	} catch {
		loggerInstance.debug(`[WebSocket] ${options.type.toUpperCase()}: non-JSON message`);
		return;
	}
	else ({payload} = options);
	const msg = shouldStringify(payload) ? JSON.stringify(payload, null, 2) : payload;
	loggerInstance.debug(`${options.type.toUpperCase()}: \n`, msg, "\n");
};
const getLogger = () => {
	const logger$1 = getLoggerInstance();
	return new Proxy(logger$1, { get(_target, prop, _receiver) {
		if (prop === "wsTraffic") return wsTraffic;
		const instance = getLoggerInstance();
		const value = Reflect.get(instance, prop);
		if (typeof value === "function") return value.bind(instance);
		return value;
	} });
};

//#endregion
//#region src/operators/filterNull.ts
/**
* RxJS operator that filters out `null` and `undefined` values with type narrowing.
*
* @example
* ```ts
* source$.pipe(filterNull()).subscribe(value => {
*   // value is guaranteed non-null
* });
* ```
*/
function filterNull() {
	return filter((value) => value != null);
}

//#endregion
//#region src/utils/getValueFrom.ts
const getValueFrom = (obj, path, defaultValue) => {
	const keys = path.split(".");
	let result = obj;
	for (const key of keys) if (result && typeof result === "object" && key in result) result = result[key];
	else return defaultValue;
	return result === void 0 ? defaultValue : result;
};

//#endregion
//#region src/operators/filterEventAs.ts
/**
* RxJS operator that filters events based on a predicate and maps matching events.
*
* This operator combines filter and map operations:
* 1. Only events that match the predicate are emitted
* 2. Matching events are transformed using the map function
*
* @template TInput - The type of input events
* @template TOutput - The type of output after mapping
* @param predicate - Function to test each event. Returns true to include the event.
* @param mapFn - Function to transform matching events
* @returns An operator function that filters and maps events
*
* @example
* ```typescript
* interface CallEvent {
*   type: 'call.started' | 'call.ended';
*   id: string;
*   timestamp: number;
* }
*
* events$.pipe(
*   isEvent(
*     (event: CallEvent) => event.type === 'call.started',
*     (event: CallEvent) => ({ id: event.id, timestamp: event.timestamp })
*   )
* ).subscribe(startEvent => {
*   console.log('Call started:', startEvent);
* });
* ```
*/
function ifIsMap(predicate, mapFn) {
	return pipe(filter$1(predicate), map$1(mapFn));
}
/**
* Generic RxJS operator that filters events using a type guard and extracts a property.
*
* This is the generic version that works with any type, not just JSONRPCRequest.
* Use this when you need to filter and extract properties from already-narrowed types.
*
* **Type inference**: The output type is automatically inferred from the input type and path!
*
* @template TInput - The type to narrow to (via type guard)
* @template TPath - The dot-notation path to extract (inferred from parameter)
* @param predicate - Type guard function to filter events
* @param resultPath - Dot-notation path to extract (e.g., 'params', 'params.data')
* @returns An operator function that filters and extracts
*
* @example
* ```typescript
* interface EventParams {
*   event_type: string;
*   data: { value: number };
* }
*
* const isAuthEvent = (e: unknown): e is EventParams =>
*   typeof e === 'object' && e !== null && 'event_type' in e;
*
* // Type of 'data' is automatically inferred as { value: number }
* params$.pipe(
*   filterAs(isAuthEvent, 'data')
* ).subscribe(data => {
*   console.log('Event data:', data.value); // TypeScript knows about .value!
* });
*
* // Deeply nested properties are also inferred
* params$.pipe(
*   filterAs(isAuthEvent, 'data.value')
* ).subscribe(value => {
*   console.log(value); // Type is 'number'
* });
* ```
*/
function filterAs(predicate, resultPath) {
	return pipe(ifIsMap(predicate, (event) => {
		return getValueFrom(event, resultPath);
	}), filter$1((value) => value !== void 0));
}

//#endregion
//#region src/operators/throwOnRPCError.ts
const logger = getLogger();
/**
* RxJS operator that throws a {@link JSONRPCError} when the RPC response contains an error.
* Passes successful responses through unchanged.
*/
function throwOnRPCError() {
	return map((response) => {
		if (response.error) {
			logger.error("[throwOnRPCError] RPC error response:", {
				code: response.error.code,
				message: response.error.message,
				data: response.error.data
			});
			throw new JSONRPCError(response.error.code, response.error.message, response.error.data);
		}
		logger.debug("[throwOnRPCError] RPC successful response:", response);
		return response;
	});
}

//#endregion
export { StorageNotAvailableError as A, VertoPongError as B, OverconstrainedFallbackError as C, RequestError as D, RecoveryError as E, UnexpectedError as F, WebSocketTimeoutError as H, UnimplementedError as I, ValidationError as L, StorageWriteError as M, TokenRefreshError as N, RequestTimeoutError as O, TransportConnectionError as P, VertoAttachHandlerError as R, MessageParseError as S, RPCTimeoutError as T, WebSocketConnectionError as V, DeviceTokenError as _, filterNull as a, JSONRPCError as b, setLogLevel as c, CallCreateError as d, CollectionFetchError as f, DeserializationError as g, DependencyError as h, getValueFrom as i, StorageReadError as j, SerializationError as k, setLogger as l, DPoPInitError as m, filterAs as n, getLogger as o, ConversationError as p, ifIsMap as r, setDebugOptions as s, throwOnRPCError as t, AuthStateHandlerError as u, InvalidCredentialsError as v, PreflightError as w, MediaTrackError as x, InvalidParams as y, VertoInviteHandlerError as z };
//# sourceMappingURL=operators-CX_lCCJm.mjs.map